import {useFormContext} from 'react-hook-form'
import {ObjectKeys} from '@/utilities/helpers'
import {formatDate} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {MedicalReportFieldsSchema} from '../formSchemas'
import {useEffect, useMemo} from 'react'
import {useGetMedicalReportByProjectKeyName} from '../queries/useGetMedicalReportByProjectKeyName'

interface UseResetMedicalReportForm {
    fieldsToKeep: (keyof MedicalReportFieldsSchema)[]
    reportDate: Date
    currentReportType: MedicalReportFieldsSchema['reportType']
    projectRefId?: string
}

export const useResetMedicalReportForm = ({
    fieldsToKeep,
    reportDate,
    currentReportType,
    projectRefId
}: UseResetMedicalReportForm) => {
    const {t} = useTranslation()
    const form = useFormContext<MedicalReportFieldsSchema>()

    const latestRenewalReportQuery = useGetMedicalReportByProjectKeyName({
        params: {
            projectId: projectRefId ?? '',
            medicalRecordReportTypeKeyName: 'renew_continuous_project'
        },
        options: {
            enabled: !!projectRefId && currentReportType.keyName == 'initial_project_assessment'
        }
    })

    const resetMedicalReportForm = (reportType: MedicalReportFieldsSchema['reportType']) => {
        const currentValues = form.getValues()

        // Create a new object with all fields set to undefined except those in fieldsToKeep
        const valuesToReset = ObjectKeys(currentValues).reduce(
            (acc, key) => ({
                ...acc,
                [key]: fieldsToKeep.includes(key) ? currentValues[key] : undefined
            }),
            {}
        )

        // Clear all form errors
        form.clearErrors()

        // Reset the form with the new values
        form.reset(valuesToReset, {
            keepDefaultValues: true,
            keepDirty: false,
            keepTouched: false
        })

        if (reportType?.keyName == 'initial_project_assessment' && !!reportDate) {
            form.setValue(
                'notes',
                t('medical_records:initial_evaluation_default_notes', {
                    date: formatDate(reportDate.toISOString(), 'DD/MM/YYYY')
                }),
                {shouldDirty: true}
            )
        }
    }

    const latestRenewalReportDate = useMemo(() => {
        return latestRenewalReportQuery.data?.date
            ? formatDate(latestRenewalReportQuery.data.date, 'DD/MM/YYYY')
            : undefined
    }, [latestRenewalReportQuery.data])

    useEffect(() => {
        if (currentReportType?.keyName == 'initial_project_assessment' && !!latestRenewalReportDate) {
            form.setValue(
                'notes',
                t('medical_records:initial_evaluation_default_notes', {
                    date: latestRenewalReportDate
                }),
                {shouldDirty: true}
            )
        }
    }, [currentReportType, latestRenewalReportDate])

    return resetMedicalReportForm
}
